<template>
  <div class="ai-chat-widget">
    <!-- Messages Container -->
    <div
      v-if="isLoading"
      class="flex justify-center bg-gray-50 p-4"
    >
      <div class="flex animate-pulse space-x-2">
        <div class="h-2 w-2 rounded-full bg-purple-400"></div>
        <div class="h-2 w-2 rounded-full bg-purple-400"></div>
        <div class="h-2 w-2 rounded-full bg-purple-400"></div>
      </div>
    </div>

    <div
      v-else-if="!hasMessages"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="40"
          height="40"
          rx="8"
          fill="#F3E8FF"
        />
        <path
          d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
          stroke="#7E22CE"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="text-md mt-5 text-center text-gray-500">Keine Nachrichten vorhanden</p>
    </div>

    <div
      v-else
      class="space-y-4p-4"
    >
      <!-- User Message (most recent) -->
      <div
        v-if="lastUserMessage"
        class="flex flex-col items-end"
      >
        <div class="ml-auto flex max-w-[85%] items-start gap-2">
          <div class="rounded-lg border border-purple-200 bg-purple-100 p-3 text-sm text-gray-800">
            {{ lastUserMessage.content }}
          </div>
          <UserAvatar
            v-if="user"
            :user="user"
          />
        </div>
        <span class="mt-1 text-xs text-gray-500">{{ formatTime(lastUserMessage.created_at) }}</span>
      </div>

      <!-- AI Response (most recent) -->
      <div
        v-if="lastAssistantMessage"
        class="overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        <div class="p-4">
          <div
            ref="messageContent"
            class="prose prose-sm max-w-none text-sm text-gray-800 dark:prose-invert prose-p:my-1 prose-pre:border
              prose-pre:border-neutral-200 prose-pre:bg-neutral-100 prose-ol:my-0.5 prose-ol:list-decimal
              prose-ol:pl-5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-5 prose-li:my-0.5 prose-li:pl-2
              prose-li:marker:text-gray-400 dark:text-white dark:prose-pre:border-neutral-700
              dark:prose-pre:bg-neutral-800 dark:prose-li:marker:text-gray-500 [&_a:hover]:text-purple-700
              [&_a]:text-purple-600 [&_a]:no-underline"
            v-html="renderedContent"
          />
        </div>
      </div>
    </div>

    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewKIChat"
      >
        KI-Chat ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>
  </div>
  <EntityCard />
  <PDFViewer
    v-model:isOpen="isPdfOpen"
    v-model:url="pdfUrl"
    :filename="pdfFilename"
    :scale="1.0"
  />
</template>

<script setup lang="ts">
  // Vue core imports
  import { computed, createApp, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'

  // Store imports
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'

  // Type imports
  import {
    AIChatSessionMessageRoleEnum,
    AiChatSessionsService,
    DocumentsService,
    type CustomerOrganisationUser
  } from '@/types/generated'

  // Component imports
  import IconRenderer from '@/components/app/chat/components/IconRenderer.vue'
  import EntityCard from '@/components/common/EntityCard.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import UserAvatar from '../../users/UserAvatar.vue'

  // Composables and utilities
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    type DocumentRegistryEntry,
    renderDocumentBadge,
    rewriteDocumentAnchorsAsBadges
  } from '@/utils/chatDocumentBadge'
  import { ChevronRightIcon } from 'lucide-vue-next'
  import MarkdownIt from 'markdown-it'

  // Translation setup
  const { t } = useTranslation({
    en: {
      Page: 'Page',
      Pages: 'Pages',
      CopyToClipboard: 'Copy to clipboard',
      Copied: 'Copied!'
    },
    de: {
      Page: 'Seite',
      Pages: 'Seiten',
      CopyToClipboard: 'In die Zwischenablage kopieren',
      Copied: 'Kopiert!'
    }
  })

  // Store and router setup
  const router = useRouter()
  const caseStore = useCaseStore()
  const { showCard } = useEntityCard()

  // Component state
  const isLoading = ref(true)
  const latestChatSession = ref(null)
  const messageContent = ref<HTMLElement | null>(null)
  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const pdfFilename = ref('')

  // Registries
  const entityRegistry = new Map()
  const documentRegistry = new Map()
  const documentRegistryByUuid = new Map<string, DocumentRegistryEntry>()
  let registryCounter = 0

  // Markdown configuration
  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  // Computed properties
  const currentCase = computed(() => caseStore.caseRecord)

  const hasMessages = computed(() => {
    return latestChatSession.value?.messages?.length > 0
  })

  const lastUserMessage = computed(() => {
    if (!hasMessages.value) return null

    const userMessages = latestChatSession.value.messages
      .filter((msg) => msg.role === AIChatSessionMessageRoleEnum.USER)
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))

    return userMessages.length > 0 ? userMessages[0] : null
  })

  const lastAssistantMessage = computed(() => {
    if (!hasMessages.value) return null

    const assistantMessages = latestChatSession.value.messages
      .filter((msg) => msg.role === AIChatSessionMessageRoleEnum.ASSISTANT)
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))

    return assistantMessages.length > 0 ? assistantMessages[0] : null
  })

  const renderedContent = computed(() => {
    entityRegistry.clear()
    documentRegistry.clear()
    documentRegistryByUuid.clear()
    registryCounter = 0

    if (!lastAssistantMessage.value) return ''
    if (!lastAssistantMessage.value.content?.chunks) {
      return rewriteAnchors(md.render(lastAssistantMessage.value.content || ''))
    }

    const content = lastAssistantMessage.value.content.chunks.reduce((acc, chunk) => {
      if (chunk.type === 'text') {
        return acc + chunk.text
      } else if (chunk.type === 'entity') {
        return acc + getEntityHtml(chunk)
      } else if (chunk.type === 'document') {
        return acc + getDocumentHtml(chunk)
      }
      return acc
    }, '')

    return rewriteAnchors(md.render(content))
  })

  // Methods
  // -- Chat session methods
  const fetchLatestChatSession = async () => {
    if (!currentCase.value?.uuid) {
      console.error('No case UUID available')
      return
    }

    if (currentCase.value.chat_sessions?.length > 0) {
      const sessionId = currentCase.value.chat_sessions[0].uuid

      try {
        const sessionData = await AiChatSessionsService.aiChatSessionsRetrieve({
          uuid: sessionId
        })
        latestChatSession.value = sessionData
      } catch (error) {
        console.error('Failed to fetch chat session details:', error)
      }
    }
  }

  const user = computed<CustomerOrganisationUser | null>(() => {
    return (
      currentCase.value?.org.users.find((user) => user.uuid === lastUserMessage.value?.user_uuid) ||
      null
    )
  })

  // -- Navigation methods
  const viewFullChat = () => {
    if (latestChatSession.value?.uuid && currentCase.value?.uuid) {
      router.push({
        name: 'case-chat',
        params: {
          caseId: currentCase.value.uuid,
          sessionId: latestChatSession.value.uuid
        }
      })
    } else if (currentCase.value?.uuid) {
      router.push({
        name: 'case-detail',
        params: { id: currentCase.value.uuid }
      })
    }
  }

  // -- Utility methods
  const formatTime = (isoTime) => {
    try {
      const date = new Date(isoTime)
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    } catch (e) {
      return ''
    }
  }

  // -- Entity and document handling methods
  const getEntityHtml = (entity) => {
    const iconHtml = renderIcon(entity.is_company ? 'building' : 'user')
    const key = `entity-${registryCounter++}`
    entityRegistry.set(key, entity)
    return `<span class="entity-link inline-flex cursor-pointer items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">${iconHtml}${entity.name}</span>`
  }

  const getDocumentHtml = (doc) => {
    const key = `doc-${registryCounter++}`
    documentRegistry.set(key, doc)
    if (doc.uuid) documentRegistryByUuid.set(doc.uuid, { ...doc, _key: key })
    const pageText =
      doc.pages.length === 0
        ? ''
        : doc.pages.length === 1
          ? `${t('Page')} ${doc.pages[0]}`
          : `${t('Pages')}: ${doc.pages.join(', ')}`
    return renderDocumentBadge(key, doc.title, renderIcon('book'), pageText)
  }

  const renderIcon = (type: 'user' | 'building' | 'book') => {
    const div = document.createElement('div')
    const app = createApp(IconRenderer, { type })
    app.mount(div)
    return div.innerHTML
  }

  const rewriteAnchors = (html: string) =>
    rewriteDocumentAnchorsAsBadges({
      html,
      registry: documentRegistryByUuid,
      renderIcon: () => renderIcon('book')
    })

  // -- Event handlers
  const handleEntityClick = (entity) => {
    showCard(entity, event.target)
  }

  const handleContainerClick = (e: Event) => {
    const target = e.target as HTMLElement
    const entityLink = target.closest('.entity-link')
    const documentLink = target.closest('.document-link')

    if (entityLink) {
      const ref = entityLink.getAttribute('data-ref')
      if (ref) {
        const entity = entityRegistry.get(ref)
        if (entity) {
          handleEntityClick(entity)
        }
      }
    }

    if (documentLink) {
      const ref = documentLink.getAttribute('data-ref')
      if (ref) {
        const doc = documentRegistry.get(ref)
        if (doc) {
          handleDocumentClick(doc)
        }
      }
    }
  }

  const handleDocumentClick = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        pdfUrl.value = response.url
        pdfFilename.value = document.title
        isPdfOpen.value = true
      }
    } catch (error) {
      console.error('Failed to fetch document:', error)
    }
  }

  // Lifecycle hooks
  onMounted(async () => {
    try {
      await fetchLatestChatSession()
    } catch (error) {
      console.error('Failed to fetch chat session:', error)
    } finally {
      isLoading.value = false
    }

    nextTick(() => {
      if (messageContent.value) {
        messageContent.value.addEventListener('click', handleContainerClick)
      }
    })
  })

  onUnmounted(() => {
    if (messageContent.value) {
      messageContent.value.removeEventListener('click', handleContainerClick)
    }
  })

  // Watchers
  watch(
    () => messageContent.value,
    () => {
      nextTick(() => {
        if (messageContent.value) {
          messageContent.value.removeEventListener('click', handleContainerClick)
          messageContent.value.addEventListener('click', handleContainerClick)
        }
      })
    },
    { deep: true }
  )

  const viewKIChat = () => {
    if (caseStore.caseRecord.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/chat`)
    }
  }
  // Provide/inject
  provide('caseData', currentCase)
</script>

<style scoped>
  .ai-chat-widget {
    max-width: 560px;
    font-family:
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Oxygen,
      Ubuntu,
      Cantarell,
      'Open Sans',
      'Helvetica Neue',
      sans-serif;
  }

  .entity-link:hover {
    background-color: rgb(243 232 255);
    border-color: rgb(216 180 254);
  }

  .document-link:hover {
    background-color: rgb(243 232 255);
    border-color: rgb(216 180 254);
  }
</style>
